import api from "./axios";

export const ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
];

/**
 * Fetch Provider cards (patient info + appt window) for a single day.
 * @param {number} providerId
 * @param {string} date yyyy-MM-dd (e.g. 2025-09-17)
 * @param {string} zone IANA zone (e.g. America/New_York)
 * @param {string[]} states e.g. ['SCHEDULED'] or ['COMPLETED','SCHEDULED_PAST']
 * @param {number} page page index
 * @param {number} size page size
 */
export async function fetchProviderAppointmentCardsByDate({
  providerId,
  date,
  zone,
  states = [],
  page = 0,
  size = 100,
}) {
  const params = { date, page, size };
  if (zone) params.zone = zone;
  if (states && states.length) params.states = states.join(",");

  const res = await api.get(
    `/api/v1/providers/${providerId}/appointments/by-date/cards`,
    { params },
  );
  return res.data; // Page<ProviderApptPatientCardView>
}

/** Lookup an appointment by provider + start (ZDT or ODT) */
export async function lookupAppointmentByStart(providerId, { start, zone }) {
  const res = await api.get(
    `/api/v1/providers/${providerId}/appointments/lookup-by-start`,
    { params: { start, zone } },
  );
  return res.data; // { appointmentId, appointmentStart, status, patientId }
}

/** Complete appointment (provider action). */
export async function completeAppointment({ appointmentId, providerId }) {
  try {
    const res = await api.patch(
      `/api/v1/providers/${providerId}/appointments/${appointmentId}/completed`,
    );
    return res.data;
  } catch (err) {
    // Preserve your existing UX message
    throw new Error("Too early: can mark no-show ≥ 1 minute after start");
  }
}

//Book appointment
export async function bookAppointmentFromSlot({
  providerId,
  slotStart,
  patientId,
  appointmentReason,
  memberId,
  appointmentType,
}) {
  const payload = {
    slotStart,
    patientId,
    appointmentReason,
    ...(memberId ? { memberId } : {}), // only include if real
    ...(appointmentType ? { appointmentType } : {}),
  };

  const res = await api.post(
    `/api/v1/providers/${providerId}/appointments/book-from-slot`,
    payload,
  );

  return res.data; // AppointmentResponse
}

/** Cancel appointment (provider action, requires reason). */
export async function cancelAppointmentProvider({
  appointmentId,
  providerId,
  reason,
}) {
  try {
    const res = await api.post(
      `/api/v1/providers/${providerId}/appointments/${appointmentId}/cancel/provider`,
      { reason },
    );
    return res.data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.detail ||
      err?.message ||
      "Cancel failed";
    throw new Error(msg);
  }
}

/** Check for conflicting SCHEDULED appointments for a patient. */
export async function checkAppointmentConflict(patientId, start, end) {
  const res = await api.get(
    `/api/v1/appointments/conflict-check/patient/${patientId}`,
    { params: { start, end } },
  );
  return res.data; // ConflictingAppointmentDto[]
}

/** Mark appointment as No Show (provider action). */
export async function markNoShow({ appointmentId, providerId }) {
  try {
    const res = await api.patch(
      `/api/v1/providers/${providerId}/appointments/${appointmentId}/no-show`,
    );
    return res.data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.detail ||
      err?.message ||
      "No-show failed";
    throw new Error(msg);
  }
}
